import { createReducer, on } from '@ngrx/store';
import { BookingInitState } from '../models/booking-reducer.interface';
import * as BookingActions from './booking.actions';  // Assuming actions are in a separate file


export const initialState: BookingInitState = {
  bookingDetails: {
    isSbbSubsidyApplied: false,
    isSbbSubsidyAvailable: false,
    bookerDataBackup: null,
    bookingType: 'new',
    bookingSelectionType: false,
    bookingProduct: 'abc',
    isD2dService: false,
    autoRefreshTime: '',
    bookingSource: null,
    bookingReference: '',
    bookingReferenceState: {
      hubdropBookingReference: null,
      simpleBookingReference: null,
    },
    bookingJourneyReference: '',
    bookingAmendSection: '',
    bookingAmendType: 'none',
    bookingJourneyTimeAmended: false,
    bookingAmendPaidByInvoice: false,
    bookingAmendPaymentFailed: false,
    bookingRequiresPayment: false,
    bookingPaid: false,
    hasCustomsAgreement: false,
    carrier: {
      originalAirlineCode: '',
      airlineCodeDisplayName: '',
      airlineCode: '',
      airlineName: '',
      operatingCode: '',
      operatingName: ''
    },
    isAirportToAirport: false,
    journeyLegDetails: [],
    journeyIncompleteId: '',
    timeslot: {
      start: '',
      end: '',
      date: '',
      id: '',
      overrideTimeSlotCapacity: false,
      airportTimeslotsWarning: false
    },
    airportTimeslot: {
      start: '',
      end: '',
      date: ''
    },
    placeId: '',
    customerAddress: {
      addressLine1: '',
      addressLine2: '',
      addressPostCode: '',
      addressTown: '',
      geoCoord: {
        latitude: '',
        longitude: ''
      }
    },
    customerDetails: {
      firstName: '',
      lastName: '',
      emailAddress: ''
    },
    customerHasMarketingEnrolment: false,
    customerReturnUrl: '',
    pnrData: {
      showPNRField: false,
      isPNRStored: false
    },
    totalPrice: '',
    premiumSurcharge: {
      charge: '',
      hasApplied: false
    },
    promo: [],
    quotePriceRequest: {},
    quote: {
      hasData: false,
      sessionId: null,
      data: [],
      flightData: {}
    },
    addBookingCalled: false,
    virtualBagCount: 0,
    bagItems: {
      bags: 1,
      childEquipment: 0,
      oversizeItem: 0
    },
    abTestingDataQuote: {
      bookingPodEnv: null,
      bookingPodVersion: null
    },
    abTestingDataForm: {
      bookingFormEnv: null,
      bookingFormVersion: null
    },
    zoneInfo: {
      zoneId: '',
      zoneDescription: ''
    },
    selectedTimeslotDateTime: '',
    uuid: '',
    reservedTimeslotId: ''
  }
};

export const bookingReducer = createReducer(
  initialState,
  on(BookingActions.updateJourneyLeg, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      journeyLegDetails: [payload]
    }
  })),

  on(BookingActions.updateBookingType, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookingType: payload
    }
  })),

  on(BookingActions.updateBookingSelectionType, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookingSelectionType: payload
    }
  })),

  on(BookingActions.updateBookingProduct, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookingProduct: payload
    }
  })),

  on(BookingActions.updateD2DService, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      isD2dService: payload
    }
  })),

  on(BookingActions.updateAutoRefreshTimeService, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      autoRefreshTime: payload
    }
  })),

  on(BookingActions.updateCarrier, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      carrier: payload
    }
  })),

  on(BookingActions.updateBookingReference, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookingReference: payload
    }
  })),

  on(BookingActions.updateBookingJourneyReference, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookingJourneyReference: payload
    }
  })),

  on(BookingActions.updateUniqueSequenceId, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      uniqueSequenceIdentifier: payload
    }
  })),

  // Continue with the rest of the actions using the `on` method in similar fashion

  on(BookingActions.updateBookingAmendSection, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookingAmendSection: payload
    }
  })),

  on(BookingActions.updateBookingAmendType, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookingAmendType: payload
    }
  })),

  on(BookingActions.updateBookingSource, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookingSource: payload
    }
  })),

  on(BookingActions.updateBookingTimeAmended, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookingJourneyTimeAmended: payload
    }
  })),

  on(BookingActions.updateBookingAmendPaidInvoice, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookingAmendPaidByInvoice: payload
    }
  })),

  on(BookingActions.updateBookingAmendPaymentFailed, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookingAmendPaymentFailed: payload
    }
  })),

  on(BookingActions.updateBookingPaid, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookingPaid: payload
    }
  })),

  on(BookingActions.updateBookingRequiresPayment, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookingRequiresPayment: payload
    }
  })),

  on(BookingActions.updateIsAirportToAirport, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      isAirportToAirport: payload
    }
  })),

  on(BookingActions.updateBookingId, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      journeyIncompleteId: payload
    }
  })),

  on(BookingActions.updateVirtualBags, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      virtualBagCount: payload
    }
  })),

  on(BookingActions.updateBagItems, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bagItems: payload
    }
  })),

  on(BookingActions.updateTotalPrice, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      totalPrice: payload
    }
  })),

  on(BookingActions.updatePremiumSurcharge, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      premiumSurcharge: payload
    }
  })),

  on(BookingActions.updateTimeslot, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      timeslot: {
        ...state.bookingDetails.timeslot, // Keep the existing timeslot properties
        start: payload.start,
        end: payload.end,
        date: payload.date,
        id: payload.id,
        overrideTimeSlotCapacity: false, // Add the missing property
        airportTimeslotsWarning: false // Add the missing property
      }
    }
  })),


  on(BookingActions.updateAirportTimeslot, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      airportTimeslot: payload
    }
  })),

  on(BookingActions.updatePlaceId, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      placeId: payload
    }
  })),

  on(BookingActions.updateCustomerAddress, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      customerAddress: payload
    }
  })),

  on(BookingActions.updateCustomerDetails, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      customerDetails: payload
    }
  })),

  on(BookingActions.updateCustomerHasMarketingEnrolment, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      customerHasMarketingEnrolment: payload
    }
  })),

  on(BookingActions.updateCustomerReturnUrl, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      customerReturnUrl: payload
    }
  })),

  on(BookingActions.updatePnrData, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      pnrData: payload
    }
  })),

  on(BookingActions.updatePromo, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      promo: [payload]
    }
  })),

  on(BookingActions.removePromo, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      promo: payload
    }
  })),

  on(BookingActions.updateQuotePriceRequest, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      quotePriceRequest: payload
    }
  })),

  on(BookingActions.updateQuote, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      quote: payload
    }
  })),

  on(BookingActions.updateAbTestQuote, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      abTestingDataQuote: payload
    }
  })),

  on(BookingActions.updateAbTestForm, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      abTestingDataForm: payload
    }
  })),

  on(BookingActions.updateAddBookingCalled, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      addBookingCalled: payload
    }
  })),

  on(BookingActions.refreshBookingState, (state, { payload }) => ({
    ...payload
  })),

  on(BookingActions.resetBookingState, () => ({
    ...initialState
  })),

  on(BookingActions.hasCustomsAgreement, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      hasCustomsAgreement: payload
    }
  })),

  on(BookingActions.zoneInfoUpdate, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      zoneInfo: payload
    }
  })),

  on(BookingActions.saveSelectedTimeslotDateTime, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      selectedTimeslotDateTime: payload
    }
  })),

  on(BookingActions.saveUuid, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      uuid: payload
    }
  })),

  on(BookingActions.updateSbbPublicTransportDiscountStatus, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      isSbbSubsidyApplied: payload
    }
  })),

  on(BookingActions.sbbSubsidyStatusUpdate, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      isSbbSubsidyAvailable: payload
    }
  })),

  on(BookingActions.updateReservedTimeslot, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      reservedTimeslotId: payload
    }
  })),

  on(BookingActions.updateBookingReferenceState, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookingReferenceState: payload
    }
  })),

  on(BookingActions.resetBookingReferenceState, (state) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookingReferenceState: {
        hubdropBookingReference: null,
        simpleBookingReference: null,
      }
    }
  })),

  on(BookingActions.bookerDataBackup, (state, { payload }) => ({
    ...state,
    bookingDetails: {
      ...state.bookingDetails,
      bookerDataBackup: payload
    }
  }))
);


